import { Link } from "react-router-dom";
import { Phone, FileText, PhoneCall, ArrowRight, Check, ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { SectionHeading, Overline, Icon, Stars, CTABand } from "../components/blocks";
import { COMPANY, SERVICES, REASSURANCE, BEFORE_AFTER, TESTIMONIALS, IMAGES } from "../lib/data";
import { trackEvent } from "../lib/api";

export default function Home() {
  return (
    <div>
      <Seo
        title="Carnier Toiture et Habitat — Couvreur & rénovation dans l'Oise (60)"
        description="Couvreur artisan dans l'Oise : rénovation de toiture, démoussage, ravalement, gouttières, isolation. 15 ans d'expérience. Devis gratuit en Oise, Île-de-France et Picardie."
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-14 md:pt-20 pb-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-neutral-200 pl-1.5 pr-4 py-1.5 shadow-sm">
                <span className="px-2.5 py-1 rounded-full bg-vegetal-600 text-white text-xs font-semibold">15 ans</span>
                <span className="text-sm text-neutral-700">d'expérience dans le bâtiment</span>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-neutral-900 leading-[1.05] text-balance">
                Protégez et embellissez votre <span className="text-terracotta-600">maison</span>, durablement.
              </h1>
            </Reveal>
            <Reveal delay={150}>
              <p className="mt-6 text-lg leading-relaxed text-neutral-700 max-w-xl">
                Artisan spécialiste de la toiture et de l'amélioration de l'habitat dans l'<strong className="text-neutral-900">Oise</strong>. Nous rénovons, entretenons et protégeons votre bien avec un travail propre et soigné.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/devis" data-testid="hero-devis-button" onClick={() => trackEvent("cta", "Accueil Hero - Devis")} className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md bg-terracotta-500 text-white hover:bg-terracotta-600 transition-colors font-medium shadow-sm">
                  <FileText className="h-4 w-4" /> Demander un devis gratuit
                </Link>
                <Link to="/rappel" data-testid="hero-rappel-button" onClick={() => trackEvent("cta", "Accueil Hero - Être rappelé")} className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md bg-white border border-neutral-200 text-neutral-900 hover:border-terracotta-400 hover:text-terracotta-600 transition-colors font-medium">
                  <PhoneCall className="h-4 w-4" /> Être rappelé
                </Link>
              </div>
            </Reveal>
            <Reveal delay={290}>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-600">
                {["Devis gratuit", "Travail soigné", "Intervention locale"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5">
                    <Check className="h-4 w-4 text-vegetal-600" /> {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={150}>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                <img src={IMAGES.heroFacade} alt="Maison rénovée dans l'Oise par Carnier Toiture et Habitat" className="w-full h-[420px] lg:h-[520px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-xl shadow-xl border border-neutral-100 px-5 py-4 flex items-center gap-4">
                <div className="grid place-items-center h-12 w-12 rounded-lg bg-terracotta-50 text-terracotta-600">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Un conseil ? Appelez-nous</p>
                  <a href={`tel:${COMPANY.phoneRaw}`} className="font-heading font-bold text-lg text-neutral-900">{COMPANY.phone}</a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-neutral-200 bg-white/60">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { k: "15 ans", v: "d'expérience" },
            { k: "3", v: "artisans dédiés" },
            { k: "10", v: "prestations bâtiment" },
            { k: "Oise", v: "+ IDF & Picardie" },
          ].map((s, i) => (
            <Reveal key={s.k} delay={i * 70}>
              <div className="text-center">
                <p className="font-heading text-3xl font-bold text-terracotta-600">{s.k}</p>
                <p className="text-sm text-neutral-600 mt-1">{s.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal>
          <SectionHeading
            center
            overline="Nos prestations"
            title="Tout pour préserver et valoriser votre habitat"
            subtitle="De la toiture à la façade, nous prenons soin de votre maison avec des solutions simples, durables et adaptées aux particuliers."
          />
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 80}>
              <Link
                to={`/prestations#${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                onClick={() => trackEvent("service", s.title)}
                className="group block h-full rounded-xl bg-white border border-neutral-200 p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-terracotta-300 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="grid place-items-center h-12 w-12 rounded-lg bg-vegetal-50 text-vegetal-600 group-hover:bg-terracotta-50 group-hover:text-terracotta-600 transition-colors">
                    <Icon name={s.icon} className="h-6 w-6" />
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-neutral-300 group-hover:text-terracotta-500 transition-colors" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold text-neutral-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{s.short}</p>
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link to="/prestations" className="inline-flex items-center gap-2 text-terracotta-600 font-medium hover:gap-3 transition-all">
            Voir toutes les prestations <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* REASSURANCE */}
      <section className="bg-vegetal-800 text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24">
          <Reveal>
            <div className="max-w-2xl">
              <Overline className="text-terracotta-300 mb-3">Pourquoi nous faire confiance</Overline>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-balance">Un artisan sérieux, proche et à votre écoute</h2>
              <p className="mt-4 text-vegetal-100/80">Une équipe à taille humaine qui traite chaque maison comme la sienne, du premier conseil jusqu'à la dernière finition.</p>
            </div>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {REASSURANCE.map((r, i) => (
              <Reveal key={r.title} delay={(i % 3) * 80}>
                <div className="rounded-xl bg-vegetal-700/40 border border-vegetal-600/40 p-6 h-full">
                  <div className="grid place-items-center h-11 w-11 rounded-lg bg-terracotta-500 text-white">
                    <Icon name={r.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-heading text-lg font-semibold">{r.title}</h3>
                  <p className="mt-2 text-sm text-vegetal-100/75 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal>
          <SectionHeading
            center
            overline="Avant / Après"
            title="Le résultat parle de lui-même"
            subtitle="Faites glisser le curseur pour découvrir la transformation. Des visuels d'illustration en attendant nos photos de chantiers réels."
          />
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {BEFORE_AFTER.map((b, i) => (
            <Reveal key={b.title} delay={i * 90}>
              <div>
                <BeforeAfterSlider before={IMAGES[b.before]} after={IMAGES[b.after]} alt={b.title} />
                <h3 className="mt-4 font-heading text-lg font-semibold text-neutral-900">{b.title}</h3>
                <p className="text-sm text-neutral-600 mt-1">{b.desc}</p>
                <span className="inline-block mt-2 text-xs font-medium text-vegetal-600 uppercase tracking-wider">{b.location}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white/60 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24">
          <Reveal>
            <SectionHeading center overline="Ils nous font confiance" title="Ce que disent nos clients" />
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 80}>
                <figure className="rounded-xl bg-white border border-neutral-200 p-6 shadow-sm h-full flex flex-col">
                  <Stars n={t.rating} />
                  <blockquote className="mt-4 text-neutral-700 leading-relaxed flex-1">« {t.text} »</blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-neutral-100">
                    <p className="font-semibold text-neutral-900">{t.name}</p>
                    <p className="text-sm text-neutral-500">{t.city}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
